import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { KbArticle, KbCollection, KbFeatureFlag, KbListResponse, KbImageUploadResponse } from '@/types/kb'

export const kbKeys = {
  all: ['kb'] as const,
  list: (params: Record<string, unknown>) => [...kbKeys.all, 'list', params] as const,
  bySlug: (slug: string) => [...kbKeys.all, 'slug', slug] as const,
  byId: (id: string) => [...kbKeys.all, 'id', id] as const,
  featureFlags: () => [...kbKeys.all, 'feature-flags'] as const,
  collections: () => [...kbKeys.all, 'collections'] as const,
  collection: (slug: string) => [...kbKeys.all, 'collections', slug] as const,
}

export function useKbArticles(params: Record<string, unknown> = {}, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: kbKeys.list(params),
    queryFn: () => api<KbListResponse>('/kb-articles', { query: params }),
    enabled,
  })
}

export function useKbArticle(slug: string) {
  return useQuery({
    queryKey: kbKeys.bySlug(slug),
    queryFn: () => api<KbArticle>(`/kb-articles/${slug}`),
    enabled: Boolean(slug),
  })
}

// Admin only: the API rejects anyone else, so callers gate it with `enabled`.
export function useKbFeatureFlags({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: kbKeys.featureFlags(),
    queryFn: () => api<KbFeatureFlag[]>('/kb-articles/feature-flags'),
    enabled,
    staleTime: 5 * 60_000,
  })
}

// Only the collections the viewer may see, in display order.
export function useKbCollections() {
  return useQuery({
    queryKey: kbKeys.collections(),
    queryFn: async () => (await api<{ data: KbCollection[] }>('/kb-collections')).data,
  })
}

export function useKbCollection(slug: string) {
  return useQuery({
    queryKey: kbKeys.collection(slug),
    queryFn: () => api<KbCollection>(`/kb-collections/${slug}`),
    enabled: Boolean(slug),
  })
}

export type KbCollectionInput = Pick<KbCollection, 'name' | 'description' | 'order'> & { companyIds: string[]; featureFlags: string[] }

export function useSaveKbCollection(id?: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbCollectionInput>) =>
      api<KbCollection>(id ? `/kb-collections/${id}` : '/kb-collections', { method: id ? 'PATCH' : 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useDeleteKbCollection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/kb-collections/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useCreateKbArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbArticle>) =>
      api<KbArticle>('/kb-articles', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useUpdateKbArticle(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<KbArticle>) =>
      api<KbArticle>(`/kb-articles/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useDeleteKbArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/kb-articles/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
  })
}

export function useUploadKbImage(articleId?: string) {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('file', file)
      const path = articleId ? `/kb-articles/${articleId}/images` : '/kb-articles/images'
      return api<KbImageUploadResponse>(path, { method: 'POST', body: fd })
    },
  })
}
